// Talks to the Cloudflare Worker that fronts the editable site content.
//
//   login()        -> exchange username/password for a session token
//   fetchSection() -> read a content section (falls back to bundled JSON)
//   saveSection()  -> persist a content section (admin only)
//
// The worker base URL comes from REACT_APP_WORKER_URL (same var as before).

import { isPreviewMode, readPreview } from "./preview";

const WORKER_URL = (process.env.REACT_APP_WORKER_URL || "").replace(/\/+$/, "");

export interface Session {
  token: string;
  username: string;
}

const assertConfigured = () => {
  if (!WORKER_URL) {
    throw new Error(
      "REACT_APP_WORKER_URL não configurado — defina a URL do Worker."
    );
  }
};

export const login = async (
  username: string,
  password: string
): Promise<Session> => {
  assertConfigured();
  const res = await fetch(`${WORKER_URL}/api/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({} as { error?: string }));
    throw new Error(data.error || "Falha ao fazer login");
  }

  return res.json();
};

// Decode the `exp` claim from our HMAC token (the part before the dot) so the
// client can drop an expired session without a round-trip. Signature is still
// verified server-side on every write.
export const isTokenExpired = (token: string): boolean => {
  try {
    const payloadB64 = token.split(".")[0];
    const b64 = payloadB64.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(json);
    return typeof payload.exp !== "number" || payload.exp < Date.now();
  } catch (e) {
    return true;
  }
};

// Resolve a stored `foto` value to a usable <img> src. Three shapes exist:
//   - a full URL (uploaded image, served by the worker)  -> use as-is
//   - an absolute path like "/pessoa_placeholder.png"     -> use as-is
//   - a bare filename ("Brena O_Dwyer.jpg", legacy photos) -> /pessoas/<file>
// Empty falls back to the shared placeholder.
export const resolveImageSrc = (foto?: string | null): string => {
  if (!foto) return "/pessoa_placeholder.png";
  if (/^https?:\/\//i.test(foto)) return foto;
  // A single leading slash is an absolute path on our own origin. Reject
  // "//host" (protocol-relative) so a foto value can't point <img> elsewhere.
  if (foto.startsWith("/") && !foto.startsWith("//")) return foto;
  return `/pessoas/${foto}`;
};

// Upload an already-resized image Blob to the worker, which stores it in KV and
// returns a permanent public URL. Admin only (Bearer token).
export const uploadImage = async (
  token: string,
  blob: Blob
): Promise<{ url: string; key: string }> => {
  assertConfigured();
  const res = await fetch(`${WORKER_URL}/api/upload`, {
    method: "POST",
    headers: {
      "Content-Type": blob.type || "image/webp",
      Authorization: `Bearer ${token}`,
    },
    body: blob,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({} as { error?: string }));
    throw new Error(data.error || "Falha ao enviar imagem");
  }
  const data = await res.json().catch(() => null);
  if (!data || typeof data.url !== "string") {
    throw new Error("Resposta inválida do servidor ao enviar imagem");
  }
  return data as { url: string; key: string };
};

// Change the admin password. Requires the current password as confirmation.
export const changePassword = async (
  token: string,
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  assertConfigured();
  const res = await fetch(`${WORKER_URL}/api/change-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({} as { error?: string }));
    throw new Error(data.error || "Falha ao alterar senha");
  }
};

// The fetched value must structurally match the bundled fallback (array vs
// object); anything else means corrupt/wrong-shape content, so we fall back
// rather than hand the caller something that will crash on .map/.sort.
const matchesShape = (value: unknown, fallback: unknown): boolean => {
  if (Array.isArray(fallback)) return Array.isArray(value);
  if (fallback !== null && typeof fallback === "object") {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }
  // fallback is null (e.g. locale hydration) — caller guards the result itself.
  return true;
};

// Reads a content section. Any failure (network, 204 "not seeded", non-OK, or a
// wrong-shaped body) resolves to `fallback` so the live site always renders.
export const fetchSection = async <T>(
  section: string,
  fallback: T
): Promise<T> => {
  // In preview mode, render the admin's stashed unsaved content instead of the
  // live copy (see services/preview.ts). Shape-checked like any other source.
  if (isPreviewMode()) {
    const preview = readPreview<T>(section);
    if (preview !== null && matchesShape(preview, fallback)) return preview;
  }
  if (!WORKER_URL) return fallback;
  try {
    const res = await fetch(`${WORKER_URL}/api/data/${section}`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (res.status === 204 || !res.ok) return fallback;
    const parsed = await res.json();
    return matchesShape(parsed, fallback) ? (parsed as T) : fallback;
  } catch (e) {
    return fallback;
  }
};

export const saveSection = async (
  token: string,
  section: string,
  data: unknown
): Promise<void> => {
  assertConfigured();
  const res = await fetch(`${WORKER_URL}/api/data/${section}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({} as { error?: string }));
    throw new Error(data.error || "Falha ao salvar alterações");
  }
};
